/**
 * 装配体配合控制器运行时服务。
 *
 * 文件职责：
 * 1. 管理 Mate PropertyManager 临时高亮；
 * 2. 解析组件运行时 Object3D 与求解状态；
 * 3. 应用和恢复配合预览 Transform。
 */

import type { Object3D } from 'three/webgpu';
import { Mesh } from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import {
  EntityInteractionEventType,
  type EntitySelectedEventPayload,
  type SelectionUpdatedEventPayload,
} from '../../manager';
import {
  areComponentInstanceSnapshotsEqual,
  applyComponentInstanceSnapshotToObject,
  ComponentInstanceTransformRootIndex,
  createComponentInstanceSnapshotFromObject,
  createComponentWorldTransformSnapshot,
  resolveComponentInstanceTransformTarget,
  type ComponentInstanceSnapshot,
  type ComponentTransformSnapshot,
} from '../component-instance';
import { AssemblyComponentFixationState } from '../component-fixation';
import { cloneComponentTransformSnapshot, type AssemblyMateDefinition, type MateEntityRef } from '../definition';
import type { AssemblyMateSolverComponentState } from '../solver';
import type { AssemblyMateControllerState } from './AssemblyMateControllerTypes';
import { cloneAssemblyMateComponentInstanceSnapshot } from './support/AssemblyMateControllerStateHelpers';
import { resolveAssemblyTopologyReferenceObject } from '../../reference/shared/resolveAssemblyTopologyReferenceObject';
import { isAssemblyMateAutoAngleReferenceEntity } from '../definition/AssemblyMateAngleReference';
import type { AssemblyMatePreviewResult } from '../preview';
import { resetImportedTopologyProxyInteractionVisual } from '../../imported-model/selection/resetImportedTopologyProxyInteractionVisual';
import type { SelectionInteractionMeta } from '../../imported-model/render/selection-extension';
import { composeAssemblyMateTransformMatrix } from './support/AssemblyMateTransformMatrix';
import { createAssemblyMateSelectionCandidatesFromSnapshot } from '../selection';
import { resolveBRepGeometryDescriptor } from '../../utils/feature/resolveBrepGeometryDescriptor';
import { resolveTopologySelectionPick } from '../../utils/feature/resolveTopologySelectionPick';
import { resolveImportedAssemblyTopologySelectionRef } from '../../imported-model/selection/resolveImportedAssemblyTopologySelectionRef';
import {
  isAssemblyReferenceSelectionComponentInstanceId,
  resolveAssemblyReferenceSelectionSnapshotEntry,
} from '../../utils/feature/resolveAssemblyReferenceSelectionSnapshot';
import { AssemblyMateFirstSelectionTransparencyController } from './AssemblyMateFirstSelectionTransparencyController';

/** 控制器状态读取函数。 */
type ResolveControllerState = () => AssemblyMateControllerState;

/** 基准引用虚拟组件的固定 identity transform。 */
const ASSEMBLY_REFERENCE_IDENTITY_TRANSFORM: ComponentTransformSnapshot = {
  position: [0, 0, 0],
  quaternion: [0, 0, 0, 1],
  scale: [1, 1, 1],
};

/**
 * 将求解器组件固定状态归一化为组件快照枚举。
 *
 * @param fixationState 求解器内部或历史数据中的固定状态。
 * @returns 组件实例快照可直接消费的固定状态枚举。
 */
function normalizeAssemblyComponentFixationState(
  fixationState: AssemblyMateSolverComponentState['fixationState'],
): AssemblyComponentFixationState {
  return fixationState === AssemblyComponentFixationState.Fixed
    ? AssemblyComponentFixationState.Fixed
    : AssemblyComponentFixationState.Floating;
}

/**
 * 预览恢复选项。
 */
export interface AssemblyMatePreviewRestoreOptions {
  /** 是否在恢复后同步 Mate 候选高亮。 */
  readonly syncHighlights?: boolean;
  /** 是否在恢复后登记普通重绘；多个预览步骤由同一 Viewer 帧合并。 */
  readonly requestRender?: boolean;
}

/**
 * 单个组件预览恢复目标。
 *
 * 设计说明：
 * 预览 apply 与 restore 之间，选择代理、命令态高亮和拓扑仓库都可能因为 UI 操作被重新解析。
 * 因此恢复不能只保存 componentInstanceId 后再次查找对象，必须保存当次实际写入的 Object3D 身份。
 */
interface AssemblyMatePreviewRestoreTarget {
  /** apply 阶段实际写入 transform 的对象。 */
  readonly targetObject: Object3D;
  /** apply 阶段对象 uuid，用于对象被重新挂载后的兜底查找。 */
  readonly targetObjectId: string;
  /** apply 前的可序列化快照。 */
  readonly snapshot: ComponentInstanceSnapshot;
}

/**
 * 装配体配合控制器运行时服务。
 */
export class AssemblyMateControllerRuntime {
  private readonly viewer: Viewer;
  private readonly resolveState: ResolveControllerState;
  private readonly previewRestoreTargets = new Map<string, AssemblyMatePreviewRestoreTarget>();
  private readonly candidateHighlightPayloads = new Map<string, EntitySelectedEventPayload>();
  private readonly candidateOrdinarySelectionObjects = new Map<string, Object3D[]>();
  private readonly firstSelectionTransparencyController: AssemblyMateFirstSelectionTransparencyController;
  private readonly transformRootIndex: ComponentInstanceTransformRootIndex;

  /**
   * 构造函数。
   *
   * @param viewer Viewer 实例。
   * @param resolveState 控制器状态读取函数。
   */
  public constructor(viewer: Viewer, resolveState: ResolveControllerState) {
    this.viewer = viewer;
    this.resolveState = resolveState;
    this.firstSelectionTransparencyController = new AssemblyMateFirstSelectionTransparencyController({
      temporaryAppearancePort: {
        setTemporaryOpacity: (ownerToken, componentObject, opacity) => (
          this.viewer.setImportedModelInstanceTemporaryOpacity?.(ownerToken, componentObject, opacity) ?? false
        ),
        isInstancedSource: object => object instanceof Mesh
          && Boolean(this.viewer.resolveImportedModelInstancedMeshUnit?.(object)),
      },
    });
    this.transformRootIndex = new ComponentInstanceTransformRootIndex({
      getScene: () => this.viewer.getScene() as Object3D | null,
      listLogicalChildren: (object) => (
        typeof this.viewer.listImportedModelSourceHierarchyChildren === 'function'
          ? this.viewer.listImportedModelSourceHierarchyChildren(object as never) as readonly Object3D[]
          : object.children
      ),
    });
  }

  /**
   * 根据组件快照恢复场景对象。
   *
   * @param snapshot 组件快照。
   * @returns 场景对象。
   */
  public resolveSceneObjectForComponentSnapshot(snapshot: ComponentInstanceSnapshot): Object3D | null {
    return this.resolveComponentObject(snapshot.componentInstanceId)
      ?? this.resolveComponentObject(snapshot.sourceNodeId);
  }

  /**
   * 解析组件求解状态。
   *
   * @param componentInstanceId 组件实例 ID。
   * @returns 组件状态。
   */
  public resolveComponentState(componentInstanceId: string): AssemblyMateSolverComponentState | null {
    const object = this.resolveComponentObject(componentInstanceId);
    if (!object) {
      if (isAssemblyReferenceSelectionComponentInstanceId(componentInstanceId)) {
        return this.createAssemblyReferenceComponentState(componentInstanceId);
      }
      return null;
    }

    const snapshot = createComponentInstanceSnapshotFromObject({
      object,
      componentInstanceId,
    });
    return {
      componentInstanceId,
      transform: snapshot.transform,
      worldTransform: createComponentWorldTransformSnapshot(object),
      fixationState: snapshot.fixationState ?? AssemblyComponentFixationState.Floating,
    };
  }

  /**
   * 解析预览应用前的组件求解状态。
   *
   * @param componentInstanceId 组件实例 ID。
   * @returns 预览快照中的组件状态；当前组件未被预览移动时返回 `null`。
   */
  public resolvePreviewRestoreComponentState(componentInstanceId: string): AssemblyMateSolverComponentState | null {
    const restoreTarget = this.previewRestoreTargets.get(componentInstanceId);
    if (!restoreTarget) {
      return null;
    }

    /**
     * 提交 Mate 时不能为了构造历史事务的 before 文档而真实回滚场景，
     * 否则会破坏 SOLIDWORKS 风格的“确认前后位姿不跳变”。这里只读取缓存快照。
     */
    return {
      componentInstanceId: restoreTarget.snapshot.componentInstanceId,
      transform: cloneComponentTransformSnapshot(restoreTarget.snapshot.transform),
      fixationState: restoreTarget.snapshot.fixationState ?? AssemblyComponentFixationState.Floating,
    };
  }

  /**
   * 按 Mate 文档中的组件状态恢复场景 transform。
   *
   * @param components Mate 文档保存的组件状态映射。
   * @returns 是否实际修改了任意组件 transform。
   */
  public applyDocumentComponentStates(
    components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  ): boolean {
    let changed = false;
    const changedObjects: Object3D[] = [];

    Object.values(components).forEach((component) => {
      const object = this.resolveComponentObject(component.componentInstanceId);
      if (!object) {
        return;
      }

      const beforeSnapshot = createComponentInstanceSnapshotFromObject({
        object,
        componentInstanceId: component.componentInstanceId,
      });
      const nextSnapshot: ComponentInstanceSnapshot = {
        ...beforeSnapshot,
        fixationState: normalizeAssemblyComponentFixationState(component.fixationState),
        transform: component.transform,
      };
      if (areComponentInstanceSnapshotsEqual(beforeSnapshot, nextSnapshot)) {
        return;
      }

      applyComponentInstanceSnapshotToObject(object as never, nextSnapshot);
      changed = true;
      changedObjects.push(object);
    });

    if (changed) {
      this.synchronizeComponentTransformEffects(changedObjects);
    }
    return changed;
  }

  /**
   * 解析组件对象。
   *
   * @param componentInstanceId 组件实例 ID。
   * @returns 组件对象。
   */
  public resolveComponentObject(componentInstanceId: string | undefined | null): Object3D | null {
    if (!componentInstanceId) {
      return null;
    }
    const indexedObject = this.transformRootIndex.resolveByObjectId(componentInstanceId)
      ?? this.transformRootIndex.resolveByAssemblyNodeId(componentInstanceId);
    if (indexedObject) {
      return indexedObject;
    }

    const fallbackObject = this.viewer.getSceneObjectById(componentInstanceId) as Object3D | null;
    const fallbackTransformTarget = resolveComponentInstanceTransformTarget(fallbackObject, {
      getScene: () => this.viewer.getScene() as Object3D | null,
      getSceneObjectById: (objectId) => this.viewer.getSceneObjectById(objectId) as Object3D | null,
    });
    /**
     * transform root 索引用于收口高频拖动路径；但测试替身、尚未挂入 scene 的运行时对象、
     * 以及部分宿主显式对象表仍需要保留 `getSceneObjectById` 兜底。兜底对象也必须先归一到
     * 组件 transform target，避免 Mate 预览把位姿写到脱离 scene 的 FeatureFace/topo 代理上。
     */
    return fallbackTransformTarget ?? fallbackObject;
  }

  /**
   * 同步一批组件位姿写回产生的装配体派生副作用。
   *
   * 设计说明：
   * 1. 正式 Viewer 统一经过 `AssemblyComponentTransformEffectsCoordinator`；
   * 2. 结构化测试替身和旧宿主没有该门面时，仍按同一顺序执行基准重算、实例矩阵更新和渲染；
   * 3. SolveService 复用本入口，避免配合预览与已提交重求解再次分叉。
   *
   * @param changedObjects 本批实际变化的组件 transform 根。
   * @param options 副作用配置。
   */
  public synchronizeComponentTransformEffects(
    changedObjects: readonly Object3D[],
    options?: PuppyCAD.AssemblyComponentTransformEffectsOptions,
  ): void {
    if (changedObjects.length === 0) {
      return;
    }
    if (typeof this.viewer.synchronizeAssemblyComponentTransforms === 'function') {
      if (options) {
        this.viewer.synchronizeAssemblyComponentTransforms(changedObjects, options);
      } else {
        this.viewer.synchronizeAssemblyComponentTransforms(changedObjects);
      }
      return;
    }

    // 兼容结构化宿主时仍保持正式协调器顺序，禁止退回“只更新实例矩阵”的旧行为。
    this.viewer.recomputeAssemblyDatumReferences?.();
    this.viewer.updateImportedModelInstanceMatrices?.(changedObjects);
    if (options?.requestRender !== false) {
      this.viewer.requestRender();
    }
  }

  /**
   * 创建基准引用虚拟组件状态。
   *
   * 设计说明：
   * SOLIDWORKS 中装配体基准点/轴/面是不可被配合求解器移动的参考对象。这里用固定 identity transform
   * 进入求解器，使“一个固定参考 + 一个浮动零件”可以正常移动浮动侧，而不会污染真实场景树。
   *
   * @param componentInstanceId 基准引用虚拟组件 ID。
   * @returns 求解器组件状态。
   */
  private createAssemblyReferenceComponentState(
    componentInstanceId: string,
  ): AssemblyMateSolverComponentState {
    return {
      componentInstanceId,
      transform: cloneComponentTransformSnapshot(ASSEMBLY_REFERENCE_IDENTITY_TRANSFORM),
      worldTransform: cloneComponentTransformSnapshot(ASSEMBLY_REFERENCE_IDENTITY_TRANSFORM),
      fixationState: AssemblyComponentFixationState.Fixed,
    };
  }

  /**
   * 按运行时对象 ID 读取原始场景对象。
   *
   * @param objectId Object3D uuid / name。
   * @returns 原始场景对象；缺失时返回 null。
   */
  public resolveSceneObjectById(objectId: string | undefined | null): Object3D | null {
    return this.transformRootIndex.resolveObjectById(objectId);
  }

  /**
   * 使组件 transform root 索引失效。
   *
   * 设计说明：
   * 项目根模型重载会复用同一个 THREE.Scene，仅替换 `imported-assembly` 子树。
   * 索引如果只按 scene 身份判断有效性，就可能继续持有已摘除旧组件，导致 Mate 文档恢复写到旧模型。
   * 因此文档加载边界必须显式失效，确保后续解析基于当前场景对象重新建立索引。
   */
  public invalidateComponentTransformRootIndex(): void {
    this.transformRootIndex.invalidate();
  }

  /**
   * 通过稳定拓扑引用重新解析 Mate 实体。
   *
   * 设计说明：
   * 1. Mate 文档保存的是拓扑稳定引用和选择瞬间几何快照，重载后不能继续信任旧 world space 几何；
   * 2. 这里仅通过 cad-core 的 imported topology 仓库按需恢复 FeatureFace/Line/Point，并立即转回可序列化描述符；
   * 3. 返回值不携带 Object3D 引用，避免文档层或 web-client 持有运行时对象。
   *
   * @param entity 已持久化的 Mate 实体引用。
   * @returns 当前场景下刷新后的 Mate 实体；无法解析时返回 `null`。
   */
  public resolveMateEntityReference(entity: MateEntityRef): MateEntityRef | null {
    if (entity.referenceRef) {
      return this.resolveReferenceMateEntityReference(entity);
    }

    const topologyRef = entity.topologyRef ?? null;
    if (!topologyRef) {
      return null;
    }

    const topologyObject = resolveAssemblyTopologyReferenceObject(this.viewer, topologyRef);
    if (!topologyObject) {
      return null;
    }
    /**
     * `.puppy` 导入后可见 body 可能挂在被折叠的局部子节点下，而 Mate 持久化引用
     * 指向外层可移动子装配体。这里必须以当前命中的 Feature 重新反推拓扑引用，
     * 保证恢复后的 componentInstanceId、topologyRef 与当前几何快照属于同一组件边界。
     */
    const refreshedTopologyPick = resolveTopologySelectionPick(topologyObject);
    const refreshedTopologyRef = refreshedTopologyPick
      ? resolveImportedAssemblyTopologySelectionRef(topologyObject, refreshedTopologyPick) ?? topologyRef
      : topologyRef;
    const geometry = resolveBRepGeometryDescriptor(topologyObject);
    if (!geometry) {
      return null;
    }

    const candidate = createAssemblyMateSelectionCandidatesFromSnapshot({
      assemblyTopologyRefs: [refreshedTopologyRef],
      brepGeometryDescriptors: [geometry],
    })[0] ?? null;
    if (!candidate) {
      return null;
    }

    const componentState = this.resolveComponentState(candidate.entityRef.componentInstanceId);
    return {
      ...candidate.entityRef,
      label: entity.label || candidate.entityRef.label,
      ...(componentState
        ? {
            componentTransformAtSelection: cloneComponentTransformSnapshot(componentState.transform),
            componentWorldTransformAtSelection: cloneComponentTransformSnapshot(
              componentState.worldTransform ?? componentState.transform,
            ),
          }
        : {}),
    };
  }

  /**
   * 通过基准稳定引用恢复 Mate 实体。
   *
   * @param entity 已持久化的 Mate 实体。
   * @returns 当前场景下刷新后的 Mate 实体；缺少基准对象时返回 `null`。
   */
  private resolveReferenceMateEntityReference(entity: MateEntityRef): MateEntityRef | null {
    const referenceObject = this.resolveAssemblyReferenceObject(entity.referenceRef);
    if (!referenceObject) {
      return null;
    }

    const referenceEntry = resolveAssemblyReferenceSelectionSnapshotEntry(referenceObject, this.viewer);
    if (!referenceEntry) {
      return null;
    }

    const candidate = createAssemblyMateSelectionCandidatesFromSnapshot({
      selectedObjectIds: [referenceEntry.objectId],
      assemblyTopologyRefs: [],
      brepGeometryDescriptors: [],
      assemblyReferenceSelectionEntries: [referenceEntry],
    })[0] ?? null;
    if (!candidate) {
      return null;
    }

    const componentState = this.resolveComponentState(candidate.entityRef.componentInstanceId);
    return {
      ...candidate.entityRef,
      label: entity.label || candidate.entityRef.label,
      ...(componentState
        ? {
            componentTransformAtSelection: cloneComponentTransformSnapshot(componentState.transform),
            componentWorldTransformAtSelection: cloneComponentTransformSnapshot(
              componentState.worldTransform ?? componentState.transform,
            ),
          }
        : {}),
    };
  }

  /**
   * 按基准引用恢复当前场景对象。
   *
   * @param referenceRef 基准引用。
   * @returns 当前正式基准对象。
   */
  private resolveAssemblyReferenceObject(
    referenceRef: AssemblyReferenceSelectionRef | undefined,
  ): Object3D | null {
    if (!referenceRef) {
      return null;
    }

    if (referenceRef.kind === 'datum-plane') {
      return (this.viewer.getDatumPlane(referenceRef.referenceId)?.getObject3D() as Object3D | undefined) ?? null;
    }
    if (referenceRef.kind === 'datum-axis') {
      return (this.viewer.getDatumAxis(referenceRef.referenceId) as Object3D | undefined) ?? null;
    }
    if (referenceRef.kind === 'datum-point') {
      return (this.viewer.getDatumPoint(referenceRef.referenceId) as Object3D | undefined) ?? null;
    }

    return null;
  }

  /**
   * 缓存本次 SelectionUpdated 对应的候选高亮快照。
   *
   * @param payload SelectionUpdated 稳定快照。
   * @param candidates 本次从快照转换出的 Mate 候选。
   */
  public cacheCandidateHighlightPayloads(
    payload: SelectionUpdatedEventPayload,
    candidates: readonly { readonly entityRef: MateEntityRef }[],
  ): void {
    const objectById = new Map<string, Object3D>();
    /**
     * SelectionUpdated 在旧调用方和部分测试夹具中可能只携带单个 object。
     * 控制器是 cad-core 的命令边界，需要在这里兼容旧快照，而不是把字段补齐责任推给 UI。
     */
    const selectionSourceObjects = Array.isArray(payload.selectionSourceObjects)
      ? payload.selectionSourceObjects
      : [];
    const selectedObjects = Array.isArray(payload.selectedObjects)
      ? payload.selectedObjects
      : [];
    [
      ...selectionSourceObjects,
      ...selectedObjects,
      payload.selectionSourceObject,
      payload.object,
    ].forEach((object) => {
      if (object) {
        objectById.set(object.uuid, object as Object3D);
      }
    });
    const pairedEntries = this.mergeSelectionSnapshotEntriesBySelectionOrder(
      payload.selectedObjectIds,
      payload.assemblyTopologySelectionEntries ?? [],
      payload.assemblyReferenceSelectionEntries ?? [],
    );

    candidates.forEach((candidate, index) => {
      const pairedObjectId = pairedEntries[index]?.objectId ?? null;
      const ordinarySelectionObjects = this.resolveCandidateOrdinarySelectionObjects({
        pairedObjectId,
        objectById,
        selectionSourceObjects,
        selectedObjects,
        payload,
        index,
      });
      this.cacheCandidateOrdinarySelectionObjects(candidate.entityRef.stableRef, ordinarySelectionObjects);

      /**
       * 局部模型替换会复用 Mate stableRef，但真实组件根和 topology proxy 已经进入新的运行时。
       * 拓扑实体的命令态高亮必须刷新当前组件根 world matrix，否则 overlay 会继续绑定旧矩阵而产生漂移。
       */
      const shouldRefreshTopologyPayload = !!candidate.entityRef.topologyRef;
      if (!shouldRefreshTopologyPayload && this.candidateHighlightPayloads.has(candidate.entityRef.stableRef)) {
        return;
      }

      const topologyProxyMatrixSourceWorldMatrix =
        this.resolveCurrentTopologyProxyMatrixSourceWorldMatrix(candidate.entityRef);
      const topologyPayload = this.createMateEntityHighlightPayload(candidate.entityRef, {
        allowComponentFallback: false,
        topologyProxyMatrixSourceWorldMatrix,
        timestamp: payload.timestamp,
      });
      if (topologyPayload) {
        this.candidateHighlightPayloads.set(candidate.entityRef.stableRef, topologyPayload);
        return;
      }

      const object = ordinarySelectionObjects[0] ?? null;
      if (!object) {
        return;
      }

      const interactionMeta = candidate.entityRef.topologyRef
        ? this.createTopologyProxyHighlightInteractionMeta(candidate.entityRef, {
            topologyProxyMatrixSourceWorldMatrix,
          })
        : payload.interactionMeta;
      if (candidate.entityRef.topologyRef && !interactionMeta) {
        return;
      }

      this.candidateHighlightPayloads.set(candidate.entityRef.stableRef, {
        type: EntityInteractionEventType.EntitySelected,
        objectId: object.uuid,
        object,
        selectedObjectIds: [object.uuid],
        selectedObjects: [object],
        interactionMeta,
        timestamp: payload.timestamp,
      });
    });
  }

  /**
   * 按当前候选列表裁剪临时高亮快照。
   *
   * @param candidates 当前 Mate 候选列表。
   */
  public pruneCandidateHighlightPayloads(
    candidates: readonly { readonly entityRef: MateEntityRef }[],
  ): void {
    const activeStableRefs = new Set(this.resolveActiveHighlightStableRefs(candidates));
    Array.from(this.candidateHighlightPayloads.keys()).forEach((stableRef) => {
      if (!activeStableRefs.has(stableRef)) {
        this.candidateHighlightPayloads.delete(stableRef);
      }
    });
    Array.from(this.candidateOrdinarySelectionObjects.keys()).forEach((stableRef) => {
      if (!activeStableRefs.has(stableRef)) {
        this.candidateOrdinarySelectionObjects.delete(stableRef);
      }
    });
  }

  /**
   * 同步当前 Mate 候选的命令态高亮。
   *
   * @param candidates 当前 Mate 候选列表。
   * @param options 高亮同步选项。
   */
  public syncCandidateHighlights(
    candidates: readonly { readonly entityRef: MateEntityRef }[],
    options: { readonly force?: boolean } = {},
  ): void {
    const entityByStableRef = this.createHighlightEntityMap(candidates);
    const payloads = this.resolveActiveHighlightStableRefs(candidates)
      .map((stableRef) => this.resolveCurrentCandidateHighlightPayload(
        stableRef,
        entityByStableRef.get(stableRef) ?? null,
      ))
      .filter((payload): payload is EntitySelectedEventPayload => payload !== null);
    this.viewer.setImportedModelCommandSelectedEntities(payloads, options);
  }

  /**
   * 清空 Mate 命令态高亮快照。
   */
  public clearCandidateHighlightPayloads(): void {
    this.candidateHighlightPayloads.clear();
    this.candidateOrdinarySelectionObjects.clear();
    this.viewer.clearImportedModelCommandSelectedEntities();
  }

  /**
   * 同步 Mate PropertyManager“使第一个选择透明”的命令态显示。
   *
   * @param candidates 当前 Mate 候选。
   * @param enabled 是否启用第一个选择透明。
   * @returns 本次调用是否修改了材质状态。
   */
  public syncFirstSelectionTransparency(
    candidates: readonly { readonly entityRef: MateEntityRef }[],
    enabled: boolean,
  ): boolean {
    const firstEntity = enabled ? candidates[0]?.entityRef ?? null : null;
    const changed = this.firstSelectionTransparencyController.sync({
      componentInstanceId: firstEntity?.componentInstanceId ?? null,
      componentObject: firstEntity ? this.resolveComponentObject(firstEntity.componentInstanceId) : null,
    });
    if (changed) {
      this.viewer.requestRender();
    }
    return changed;
  }

  /**
   * 释放 Mate 运行时持有的临时显示资源。
   */
  public dispose(): void {
    if (this.firstSelectionTransparencyController.clear()) {
      this.viewer.requestRender();
    }
    this.firstSelectionTransparencyController.dispose();
    this.transformRootIndex.dispose();
  }

  /**
   * 清理普通选择通道，保留 Mate 命令态高亮由后续同步重建。
   */
  public clearOrdinarySelectionForCandidateEdit(
    candidates: readonly { readonly entityRef: MateEntityRef }[] = [],
    options: { readonly defer?: boolean } = {},
  ): void {
    /**
     * 删除 PropertyManager 槽位是 Mate 命令内部状态变化，不应让普通 selectedPicks
     * 继续保存最后一次场景点击对象；否则用户右键删除后重新选择时会被旧选择状态干扰。
     */
    const ordinarySelectionVisualObjects = this.resolveOrdinarySelectionVisualObjects(candidates);
    const clearOrdinarySelection = () => {
      this.viewer.clearSelectionProgrammatically?.();
      this.resetOrdinarySelectionVisualObjects(ordinarySelectionVisualObjects);
      this.viewer.requestRender();
    };
    if (options.defer) {
      /**
       * EntitySelectionManager 会先发布 SelectionUpdated，再在同一次点击流程末尾发布 EntitySelected。
       * Mate 正是在 SelectionUpdated 阶段吸收候选；若此处同步清理，随后原始 EntitySelected 会把
       * FeatureFace 本体材质重新刷回 selected，形成用户看到的“蓝面脱离实体”。
       */
      queueMicrotask(clearOrdinarySelection);
      return;
    }
    clearOrdinarySelection();
  }

  /**
   * 创建已提交 Mate 的命令态高亮事件。
   *
   * @param mate 需要高亮的 Mate Definition。
   * @returns 可交给 imported-model highlighter 的事件快照。
   */
  public createMateHighlightPayloads(mate: AssemblyMateDefinition): readonly EntitySelectedEventPayload[] {
    return this.resolveMateHighlightEntities(mate)
      .map((entity) => this.createMateEntityHighlightPayloadFromCurrentRuntime(entity))
      .filter((payload): payload is EntitySelectedEventPayload => payload !== null);
  }

  /**
   * 缓存已提交 Mate 的编辑会话高亮快照。
   *
   * @param mate 正在编辑的 Mate Definition。
   */
  public cacheMateDefinitionHighlightPayloads(mate: AssemblyMateDefinition): void {
    this.resolveMateHighlightEntities(mate).forEach((entity) => {
      const payload = this.createMateEntityHighlightPayloadFromCurrentRuntime(entity);
      if (!payload) {
        return;
      }
      this.candidateHighlightPayloads.set(entity.stableRef, payload);
    });
  }

  /**
   * 应用预览 transform。
   *
   * @param preview 预览结果。
   * @param candidates 当前候选，用于恢复命令态高亮。
   */
  public applyPreviewTransforms(
    preview: AssemblyMatePreviewResult,
    candidates: readonly { readonly entityRef: MateEntityRef }[],
  ): void {
    const changedObjects: Object3D[] = [];
    Object.entries(preview.componentTransforms).forEach(([componentInstanceId, transform]) => {
      const cachedRestoreTarget = this.previewRestoreTargets.get(componentInstanceId);
      const object = cachedRestoreTarget?.targetObject ?? this.resolveComponentObject(componentInstanceId);
      if (!object) {
        return;
      }
      const beforeSnapshot = cachedRestoreTarget?.snapshot
        ?? createComponentInstanceSnapshotFromObject({ object, componentInstanceId });
      if (!cachedRestoreTarget) {
        this.previewRestoreTargets.set(componentInstanceId, {
          targetObject: object,
          targetObjectId: object.uuid,
          snapshot: cloneAssemblyMateComponentInstanceSnapshot(beforeSnapshot),
        });
      }
      this.applyTransformToObject(object, beforeSnapshot, transform);
      changedObjects.push(object);
    });
    this.syncCandidateHighlights(candidates, { force: true });
    this.synchronizeComponentTransformEffects(changedObjects);
  }

  /**
   * 判断当前是否存在待提交或待回滚的预览快照。
   *
   * @returns 存在预览快照时返回 `true`。
   */
  public hasPreviewSnapshot(): boolean {
    return this.previewRestoreTargets.size > 0;
  }

  /**
   * 恢复预览。
   *
   * @param candidates 当前候选，用于恢复命令态高亮。
   * @param options 恢复选项。
   * @returns 是否实际回滚过预览 transform。
   */
  public restorePreview(
    candidates: readonly { readonly entityRef: MateEntityRef }[],
    options: AssemblyMatePreviewRestoreOptions = {},
  ): boolean {
    const syncHighlights = options.syncHighlights ?? true;
    const requestRender = options.requestRender ?? true;

    if (this.previewRestoreTargets.size === 0) {
      if (syncHighlights) {
        this.syncCandidateHighlights(candidates, { force: true });
      }
      return false;
    }

    const changedObjects: Object3D[] = [];
    this.previewRestoreTargets.forEach((restoreTarget) => {
      const object = this.resolvePreviewRestoreObject(restoreTarget);
      if (!object) {
        return;
      }
      applyComponentInstanceSnapshotToObject(object as never, restoreTarget.snapshot);
      changedObjects.push(object);
    });
    this.synchronizeComponentTransformEffects(changedObjects, { requestRender: false });
    this.clearPreviewSnapshotOnly();
    if (syncHighlights) {
      this.syncCandidateHighlights(candidates, { force: true });
    }
    if (requestRender) {
      this.viewer.requestRender();
    }
    return true;
  }

  /**
   * 清理预览快照但不回滚对象。
   */
  public clearPreviewSnapshotOnly(): void {
    this.previewRestoreTargets.clear();
  }

  /**
   * 解析当前需要保持命令态高亮的稳定引用。
   *
   * @param candidates 当前 Mate 候选列表。
   * @returns 需要高亮的稳定引用集合。
   */
  private resolveActiveHighlightStableRefs(
    candidates: readonly { readonly entityRef: MateEntityRef }[],
  ): readonly string[] {
    const stableRefs = candidates.map((candidate) => candidate.entityRef.stableRef);
    const referenceStableRef = this.resolveState().angleReferenceEntity?.stableRef;
    if (!referenceStableRef || stableRefs.includes(referenceStableRef)) {
      return stableRefs;
    }
    return [...stableRefs, referenceStableRef];
  }

  /**
   * 创建当前命令态高亮实体索引。
   *
   * @param candidates 当前 Mate 候选列表。
   * @returns 按稳定引用索引的 Mate 实体集合。
   */
  private createHighlightEntityMap(
    candidates: readonly { readonly entityRef: MateEntityRef }[],
  ): Map<string, MateEntityRef> {
    const entityByStableRef = new Map<string, MateEntityRef>();
    candidates.forEach((candidate) => {
      entityByStableRef.set(candidate.entityRef.stableRef, candidate.entityRef);
    });

    const referenceEntity = this.resolveState().angleReferenceEntity;
    if (referenceEntity) {
      entityByStableRef.set(referenceEntity.stableRef, referenceEntity);
    }

    return entityByStableRef;
  }

  /**
   * 解析当前 Mate 候选占用过的普通选择视觉对象。
   *
   * @param candidates 当前 Mate 候选列表。
   * @returns 需要恢复普通交互视觉的对象快照。
   */
  private resolveOrdinarySelectionVisualObjects(
    candidates: readonly { readonly entityRef: MateEntityRef }[],
  ): readonly Object3D[] {
    const entityByStableRef = this.createHighlightEntityMap(candidates);
    const visitedObjectIds = new Set<string>();
    const objects: Object3D[] = [];

    this.resolveActiveHighlightStableRefs(candidates).forEach((stableRef) => {
      const entity = entityByStableRef.get(stableRef) ?? null;
      const payload = entity
        ? this.createMateEntityHighlightPayload(entity, { allowComponentFallback: false })
        : null;
      const resolvedPayload = payload ?? this.candidateHighlightPayloads.get(stableRef) ?? null;
      if (!resolvedPayload) {
        return;
      }

      /**
       * 命令态 overlay 和普通选择材质是两条通道。这里只清 Feature 代理本体，
       * 避免误改组件根、GLB 主网格或其它非拓扑对象的正常材质。
       */
      [
        ...(this.candidateOrdinarySelectionObjects.get(stableRef) ?? []),
        ...(resolvedPayload.object ? [resolvedPayload.object] : []),
        ...resolvedPayload.selectedObjects,
      ].forEach((object) => {
        if (!object || visitedObjectIds.has(object.uuid)) {
          return;
        }
        visitedObjectIds.add(object.uuid);
        objects.push(object as Object3D);
      });
    });
    return objects;
  }

  /**
   * 重置指定对象的普通选择视觉。
   *
   * @param objects 需要恢复普通交互视觉的对象快照。
   */
  private resetOrdinarySelectionVisualObjects(objects: readonly Object3D[]): void {
    objects.forEach((object) => {
      resetImportedTopologyProxyInteractionVisual(object);
    });
  }

  /**
   * 按真实选择顺序合并拓扑和基准引用快照。
   *
   * @param selectedObjectIds SelectionUpdated 中的对象顺序。
   * @param topologyEntries 拓扑实体快照。
   * @param referenceEntries 基准引用快照。
   * @returns 已排序快照。
   */
  private mergeSelectionSnapshotEntriesBySelectionOrder(
    selectedObjectIds: readonly string[] | undefined | null,
    topologyEntries: readonly { readonly objectId: string }[],
    referenceEntries: readonly { readonly objectId: string }[],
  ): Array<{ readonly objectId: string }> {
    const entries = [...topologyEntries, ...referenceEntries];
    const orderedSelectedObjectIds = selectedObjectIds ?? [];
    if (orderedSelectedObjectIds.length === 0) {
      return entries;
    }

    const orderByObjectId = new Map(orderedSelectedObjectIds.map((objectId, index) => [objectId, index]));
    return entries.sort((left, right) => {
      const leftOrder = orderByObjectId.get(left.objectId) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = orderByObjectId.get(right.objectId) ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    });
  }

  /**
   * 解析候选在普通选择通道中实际触碰过的对象。
   *
   * 设计说明：
   * 1. 命令态高亮会优先通过稳定拓扑引用重新创建 FeatureFace / FeatureLine / FeaturePoint；
   * 2. 普通选择材质却可能已经写在“本次鼠标点击产生的旧代理”上，二者不是同一个 Object3D；
   * 3. 因此必须把实际点击对象单独记录下来，只用于清理普通 selected/hover 视觉，避免预览后出现蓝色面与实体分离。
   *
   * @param input SelectionUpdated 与候选槽位上下文。
   * @returns 需要在命令吸收后恢复默认材质的对象列表。
   */
  private resolveCandidateOrdinarySelectionObjects(input: {
    readonly pairedObjectId: string | null;
    readonly objectById: ReadonlyMap<string, Object3D>;
    readonly selectionSourceObjects: readonly Object3D[];
    readonly selectedObjects: readonly Object3D[];
    readonly payload: SelectionUpdatedEventPayload;
    readonly index: number;
  }): readonly Object3D[] {
    return [
      input.pairedObjectId ? input.objectById.get(input.pairedObjectId) ?? null : null,
      input.selectionSourceObjects[input.index] ?? null,
      input.selectedObjects[input.index] ?? null,
      input.payload.selectionSourceObject as Object3D | null | undefined,
      input.payload.object as Object3D | null | undefined,
    ].filter((object): object is Object3D => Boolean(object));
  }

  /**
   * 缓存候选对应的普通选择视觉对象。
   *
   * @param stableRef Mate 实体稳定引用。
   * @param objects 本次 SelectionUpdated 中实际被普通选择链触碰的对象。
   */
  private cacheCandidateOrdinarySelectionObjects(
    stableRef: string,
    objects: readonly Object3D[],
  ): void {
    if (objects.length === 0) {
      return;
    }

    const existingObjects = this.candidateOrdinarySelectionObjects.get(stableRef) ?? [];
    const objectById = new Map(existingObjects.map((object) => [object.uuid, object]));
    objects.forEach((object) => {
      objectById.set(object.uuid, object);
    });
    this.candidateOrdinarySelectionObjects.set(stableRef, [...objectById.values()]);
  }

  /**
   * 解析当前应交给 imported-model 高亮器的事件快照。
   *
   * 设计说明：
   * 1. imported FeatureFace/FeatureLine/FeaturePoint 是按需创建的临时拓扑代理；
   * 2. 普通选择系统在下一次命中后可能回收上一次代理，不能长期复用缓存的 Object3D；
   * 3. 因此每次同步命令态高亮都优先按 MateEntityRef 重新解析当前对象，并保留旧快照中的
   *    primitive 高亮目标作为精确兜底，避免退化成只高亮最后一次点击的实体。
   *
   * @param stableRef Mate 实体稳定引用。
   * @param entity 当前 Mate 实体引用；为空时只能回退到旧缓存。
   * @returns 可用于命令态高亮的事件快照。
   */
  private resolveCurrentCandidateHighlightPayload(
    stableRef: string,
    entity: MateEntityRef | null,
  ): EntitySelectedEventPayload | null {
    const cachedPayload = this.candidateHighlightPayloads.get(stableRef) ?? null;
    if (!entity) {
      return cachedPayload;
    }

    const cachedTopologyProxyMatrixSourceWorldMatrix =
      cachedPayload?.interactionMeta?.topologyProxyMatrixSourceWorldMatrix;
    const fallbackTopologyProxyMatrixSourceWorldMatrix = entity.topologyRef
      ? this.resolveSelectionComponentWorldMatrixElements(entity.componentInstanceId)
      : undefined;
    const refreshedPayload = this.createMateEntityHighlightPayload(entity, {
      /**
       * 拓扑候选已有点击瞬间缓存时，不允许退化为组件根。
       * 否则 imported pick host 暂时不可恢复时会把命令态 selected overlay 绑定到整件，
       * 继续触发“高亮面与实体分离”或“只剩最后一个面高亮”的视觉错误。
       */
      allowComponentFallback: !entity.topologyRef || !cachedPayload,
      /**
       * detached FeatureFace/Line/Point 代理通常停留在选择瞬间的 world matrix。
       * 这里必须优先复用选择瞬间的组件矩阵基线，再由 highlighter 绑定到真实组件根当前矩阵；
       * 如果在预览后把基线刷新为当前组件 world matrix，反算 localMatrixToSource 会抵消预览位移，
       * 表现为蓝色面块移动到错误位置而实体仍在原地。缺少缓存时才读取当前矩阵，保证重载/编辑入口仍可恢复。
       */
      topologyProxyMatrixSourceWorldMatrix: cachedTopologyProxyMatrixSourceWorldMatrix
        ?? fallbackTopologyProxyMatrixSourceWorldMatrix,
      timestamp: cachedPayload?.timestamp,
    });
    if (!refreshedPayload) {
      return cachedPayload;
    }

    const shouldReuseCachedInteractionMeta = !entity.topologyRef
      && cachedPayload?.interactionMeta
      && !refreshedPayload.interactionMeta;
    /**
     * 配合命令选中的面/线/点必须以稳定拓扑引用恢复出的代理对象为准。
     * 普通 SelectionUpdated 的 interactionMeta 只描述“当次鼠标命中”的 GLB primitive，
     * 在配合预览或拖动更新 transform 后继续复用会把 overlay 绑到旧命中目标，
     * 造成蓝色高亮面和真实实体分离；因此拓扑实体禁止继承该缓存元数据。
     */
    const payload = shouldReuseCachedInteractionMeta
      ? {
          ...refreshedPayload,
          interactionMeta: cachedPayload.interactionMeta,
        }
      : refreshedPayload;
    this.candidateHighlightPayloads.set(stableRef, payload);
    return payload;
  }

  /**
   * 创建单个 Mate 实体的命令态高亮事件。
   *
   * @param entity Mate 实体引用。
   * @returns 高亮事件；当前场景无法恢复对象时返回 null。
   */
  private createMateEntityHighlightPayload(
    entity: MateEntityRef,
    options: {
      readonly allowComponentFallback?: boolean;
      readonly topologyProxyMatrixSourceWorldMatrix?: readonly number[];
      readonly timestamp?: number;
    } = {},
  ): EntitySelectedEventPayload | null {
    const topologyObject = entity.topologyRef
      ? resolveAssemblyTopologyReferenceObject(this.viewer, entity.topologyRef)
      : null;
    const referenceObject = entity.referenceRef
      ? this.resolveAssemblyReferenceObject(entity.referenceRef)
      : null;
    const object = topologyObject
      ?? referenceObject
      ?? (options.allowComponentFallback ?? true
        ? this.resolveComponentObject(entity.componentInstanceId)
        : null);
    if (!object) {
      return null;
    }
    const selectedObject = object as NonNullable<EntitySelectedEventPayload['object']>;
    const interactionMeta = topologyObject
      ? this.createTopologyProxyHighlightInteractionMeta(entity, {
          topologyProxyMatrixSourceWorldMatrix: options.topologyProxyMatrixSourceWorldMatrix,
        })
      : undefined;
    if (topologyObject && !interactionMeta) {
      return null;
    }

    return {
      type: EntityInteractionEventType.EntitySelected,
      objectId: selectedObject.uuid,
      object: selectedObject,
      selectedObjectIds: [selectedObject.uuid],
      selectedObjects: [selectedObject],
      interactionMeta,
      timestamp: options.timestamp ?? Date.now(),
    };
  }

  /**
   * 基于当前运行时创建已提交 Mate 的高亮 payload。
   *
   * @param entity Mate 实体引用。
   * @returns 可用于命令态高亮的 payload；当前组件矩阵缺失时返回 `null`。
   */
  private createMateEntityHighlightPayloadFromCurrentRuntime(
    entity: MateEntityRef,
  ): EntitySelectedEventPayload | null {
    const topologyProxyMatrixSourceWorldMatrix =
      this.resolveCurrentTopologyProxyMatrixSourceWorldMatrix(entity);
    if (entity.topologyRef && !topologyProxyMatrixSourceWorldMatrix) {
      return null;
    }

    /**
     * 已提交 Mate 来自持久化文档，里面的 selection 矩阵可能属于被替换前的运行时。
     * 因此配合树高亮和编辑缓存只允许绑定当前组件根，避免“切换失败/未重载前”的旧坐标残留。
     */
    return this.createMateEntityHighlightPayload(entity, {
      topologyProxyMatrixSourceWorldMatrix,
    });
  }

  /**
   * 读取当前运行时中拓扑代理应绑定的组件根 world matrix。
   *
   * @param entity Mate 实体引用。
   * @returns 当前组件根 world matrix；非拓扑实体或组件缺失时返回 `undefined`。
   */
  private resolveCurrentTopologyProxyMatrixSourceWorldMatrix(
    entity: MateEntityRef,
  ): readonly number[] | undefined {
    if (!entity.topologyRef) {
      return undefined;
    }

    /**
     * Mate Definition 里保存的是选择瞬间的矩阵快照，只能作为无法解析当前组件时的兜底。
     * 局部模型替换和提交后重载都会重建 Object3D，命令态高亮必须优先读取当前组件根，
     * 否则已提交配合或候选配合会把 topology overlay 反算到旧运行时坐标。
     */
    return this.resolveSelectionComponentWorldMatrixElements(entity.componentInstanceId);
  }

  /**
   * 创建 imported 拓扑代理命令态高亮需要的交互元信息。
   *
   * 设计说明：
   * 1. FeatureFace / FeatureLine / FeaturePoint 代理对象可能不挂在真实组件根下；
   * 2. Mate 预览会移动组件根，但代理对象可能仍保留旧世界矩阵或被重新挂回组件根；
   * 3. 因此高亮层必须使用调用方传入的组件根 world matrix 计算代理局部矩阵，再绑定到当前组件根。
   *
   * @param entity Mate 实体引用。
   * @returns 命令态拓扑高亮元信息；缺少可用组件 transform 时返回 `undefined`。
   */
  private createTopologyProxyHighlightInteractionMeta(
    entity: MateEntityRef,
    options: {
      readonly topologyProxyMatrixSourceWorldMatrix?: readonly number[];
    } = {},
  ): SelectionInteractionMeta | undefined {
    const matrixElements = options.topologyProxyMatrixSourceWorldMatrix
      ?? (entity.componentWorldTransformAtSelection
        ? [...composeAssemblyMateTransformMatrix(entity.componentWorldTransformAtSelection).elements]
        : entity.componentTransformAtSelection
          ? [...composeAssemblyMateTransformMatrix(entity.componentTransformAtSelection).elements]
          : undefined);
    const matrixSourceObject = this.resolveComponentObject(entity.componentInstanceId);
    if (!matrixElements) {
      return undefined;
    }

    return {
      source: 'programmatic',
      semantic: 'subentity',
      topologyProxyMatrixSourceWorldMatrix: [...matrixElements],
      topologyProxyMatrixSourceObjectId: matrixSourceObject?.uuid,
    };
  }

  /**
   * 解析组件根当前世界矩阵。
   *
   * 设计说明：
   * 1. `componentTransformAtSelection` 是组件 local transform，不能在子装配或父级节点存在 transform 时当作 world matrix；
   * 2. imported 拓扑代理可能脱离真实组件根，高亮器需要用组件根当前 world matrix 反算代理局部矩阵；
   * 3. 该矩阵只进入命令态高亮 payload，不写入 Mate Definition，避免把视觉状态污染到持久化业务数据；
   * 4. 预览和拖动求解后必须重新读取当前 world matrix，否则 overlay 会继续绑定选择瞬间旧矩阵。
   *
   * @param componentInstanceId 组件实例 ID。
   * @returns 16 个矩阵元素；无法解析组件根时返回 `undefined`。
   */
  private resolveSelectionComponentWorldMatrixElements(
    componentInstanceId: string,
  ): readonly number[] | undefined {
    const componentObject = this.resolveComponentObject(componentInstanceId);
    if (!componentObject) {
      return undefined;
    }

    componentObject.updateWorldMatrix(true, false);
    return [...componentObject.matrixWorld.elements];
  }

  /**
   * 解析 Mate 需要高亮的实体。
   *
   * @param mate Mate Definition。
   * @returns 需要高亮的实体列表。
   */
  private resolveMateHighlightEntities(mate: AssemblyMateDefinition): readonly MateEntityRef[] {
    return [
      mate.firstEntity,
      mate.secondEntity,
      ...(
        mate.referenceEntity && !isAssemblyMateAutoAngleReferenceEntity(mate.referenceEntity)
          ? [mate.referenceEntity]
          : []
      ),
    ];
  }

  /**
   * 应用 transform 到对象。
   *
   * @param object 组件对象。
   * @param baseSnapshot 基线快照。
   * @param transform 目标 transform。
   */
  private applyTransformToObject(
    object: Object3D,
    baseSnapshot: ComponentInstanceSnapshot,
    transform: ComponentTransformSnapshot,
  ): void {
    applyComponentInstanceSnapshotToObject(object as never, {
      ...baseSnapshot,
      transform,
    });
  }

  /**
   * 判断对象是否仍挂在当前 Viewer 场景中。
   *
   * @param object 候选对象。
   * @returns 是否仍属于当前场景树。
   */
  private isObjectAttachedToCurrentScene(object: Object3D): boolean {
    const scene = this.viewer.getScene() as Object3D | null;
    if (!scene) {
      return Boolean(object.parent);
    }

    let current: Object3D | null = object;
    while (current) {
      if (current === scene) {
        return true;
      }
      current = current.parent;
    }
    return false;
  }

  /**
   * 解析预览恢复对象。
   *
   * @param restoreTarget apply 阶段记录的恢复目标。
   * @returns 应恢复的场景对象；无法解析时返回 null。
   */
  private resolvePreviewRestoreObject(restoreTarget: AssemblyMatePreviewRestoreTarget): Object3D | null {
    if (this.isObjectAttachedToCurrentScene(restoreTarget.targetObject)) {
      return restoreTarget.targetObject;
    }

    const objectByRuntimeId = this.viewer.getSceneObjectById(restoreTarget.targetObjectId) as Object3D | null;
    if (objectByRuntimeId) {
      return objectByRuntimeId;
    }

    /**
     * 对象被销毁或重建时才回退到快照稳定 ID。正常预览切换必须优先恢复 apply 阶段的对象，
     * 避免同 assemblyNodeId 的拓扑代理和真实网格分别残留不同 transform。
     */
    return this.resolveSceneObjectForComponentSnapshot(restoreTarget.snapshot);
  }
}
